import type { U340xKpiMetricConfig } from '../types/u340xKpiTypes';
import type {
  U340xKpiDetailColumnDef,
  U340xKpiLogicalUnitCode,
  U340xKpiMonthDetailRow,
} from '../types/u340xKpiLuTypes';

export type U340xKpiMetricKeys = {
  primaryKey: string;
  secondaryKey: string;
  secondaryFormat: 'currency' | 'number';
  primaryLabelKey: string;
  secondaryLabelKey: string;
  /** Barre empilée (heures non facturables U3402 / write-off count U3403). */
  primaryStackKey?: string;
  primaryStackLabelKey?: string;
  /** Ligne $ supplémentaire (non facturable U3402 / write-off HT U3403). */
  secondaryExtraKey?: string;
  secondaryExtraLabelKey?: string;
  /** Ligne TEC STANDARD (U3403). */
  secondaryTecKey?: string;
  secondaryTecLabelKey?: string;
  /** Ligne TEC write-off (U3403). */
  secondaryTecExtraKey?: string;
  secondaryTecExtraLabelKey?: string;
};

const METRIC_KEYS: Record<U340xKpiLogicalUnitCode, U340xKpiMetricKeys> = {
  U3401: {
    primaryKey: 'projectCount',
    secondaryKey: 'budgetAmount',
    secondaryFormat: 'currency',
    primaryLabelKey: 'metrics.projectCount',
    secondaryLabelKey: 'metrics.budgetAmount',
  },
  U3402: {
    primaryKey: 'billableHours',
    secondaryKey: 'billableAmount',
    secondaryFormat: 'currency',
    primaryLabelKey: 'metrics.billableHours',
    secondaryLabelKey: 'metrics.billableAmount',
    primaryStackKey: 'nonBillableHours',
    primaryStackLabelKey: 'metrics.nonBillableHours',
    secondaryExtraKey: 'nonBillableAmount',
    secondaryExtraLabelKey: 'metrics.nonBillableAmount',
  },
  U3403: {
    primaryKey: 'invoiceCount',
    secondaryKey: 'amountHt',
    secondaryFormat: 'currency',
    primaryLabelKey: 'metrics.invoiceCount',
    secondaryLabelKey: 'metrics.amountHt',
    /** TEC STANDARD uniquement sur le chart (write-off = filtre détail seulement). */
    secondaryTecKey: 'amountTec',
    secondaryTecLabelKey: 'metrics.amountTec',
  },
};

const DETAIL_COLUMNS: Record<U340xKpiLogicalUnitCode, U340xKpiDetailColumnDef[]> = {
  U3401: [
    { key: 'projetNumber', labelKey: 'detail.projetNumber' },
    { key: 'projetName', labelKey: 'detail.projetName' },
    { key: 'fixedAmount', labelKey: 'detail.fixedAmount', format: 'currency', align: 'right' },
    { key: 'createdAt', labelKey: 'detail.createdAt', format: 'date' },
  ],
  U3402: [
    { key: 'projetNumber', labelKey: 'detail.projetNumber' },
    { key: 'projetName', labelKey: 'detail.projetName' },
    { key: 'billable', labelKey: 'detail.billable', format: 'boolean', align: 'center' },
    {
      key: 'billableQuantity',
      labelKey: 'detail.billableQuantity',
      format: 'number',
      align: 'right',
    },
    {
      key: 'nonBillableQuantity',
      labelKey: 'detail.nonBillableQuantity',
      format: 'number',
      align: 'right',
    },
    {
      key: 'billableCostAmount',
      labelKey: 'detail.billableCostAmount',
      format: 'currency',
      align: 'right',
    },
    {
      key: 'nonBillableCostAmount',
      labelKey: 'detail.nonBillableCostAmount',
      format: 'currency',
      align: 'right',
    },
  ],
  U3403: [
    { key: 'invoiceNumber', labelKey: 'detail.invoiceNumber' },
    { key: 'invoiceDate', labelKey: 'detail.invoiceDate', format: 'date' },
    { key: 'amountHt', labelKey: 'detail.amountHt', format: 'currency', align: 'right' },
    { key: 'amountTec', labelKey: 'detail.amountTec', format: 'currency', align: 'right' },
    { key: 'status', labelKey: 'detail.status' },
    { key: 'documentNature', labelKey: 'detail.documentNature' },
  ],
};

export function u340xMetricKeysForCode(code: U340xKpiLogicalUnitCode): U340xKpiMetricKeys {
  return METRIC_KEYS[code];
}

/** Construit la config dual-métrique pour U340xKpiTile (optionnellement 4 séries U3402). */
export function u340xBuildMetricConfig(
  code: U340xKpiLogicalUnitCode,
  t: (key: string) => string,
): {
  primaryMetric: U340xKpiMetricConfig & { axis: 'left' };
  secondaryMetric: U340xKpiMetricConfig & { axis: 'right' };
  primaryStackMetric?: U340xKpiMetricConfig & { axis: 'left' };
  secondaryExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
} {
  const keys = u340xMetricKeysForCode(code);
  const result: {
    primaryMetric: U340xKpiMetricConfig & { axis: 'left' };
    secondaryMetric: U340xKpiMetricConfig & { axis: 'right' };
    primaryStackMetric?: U340xKpiMetricConfig & { axis: 'left' };
    secondaryExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
    secondaryTecMetric?: U340xKpiMetricConfig & { axis: 'right' };
    secondaryTecExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  } = {
    primaryMetric: {
      key: keys.primaryKey,
      label: t(keys.primaryLabelKey),
      axis: 'left',
    },
    secondaryMetric: {
      key: keys.secondaryKey,
      label: t(keys.secondaryLabelKey),
      axis: 'right',
      format: keys.secondaryFormat,
    },
  };

  if (keys.primaryStackKey && keys.primaryStackLabelKey) {
    result.primaryStackMetric = {
      key: keys.primaryStackKey,
      label: t(keys.primaryStackLabelKey),
      axis: 'left',
    };
  }
  if (keys.secondaryExtraKey && keys.secondaryExtraLabelKey) {
    result.secondaryExtraMetric = {
      key: keys.secondaryExtraKey,
      label: t(keys.secondaryExtraLabelKey),
      axis: 'right',
      format: keys.secondaryFormat,
    };
  }
  if (keys.secondaryTecKey && keys.secondaryTecLabelKey) {
    result.secondaryTecMetric = {
      key: keys.secondaryTecKey,
      label: t(keys.secondaryTecLabelKey),
      axis: 'right',
      format: keys.secondaryFormat,
    };
  }
  if (keys.secondaryTecExtraKey && keys.secondaryTecExtraLabelKey) {
    result.secondaryTecExtraMetric = {
      key: keys.secondaryTecExtraKey,
      label: t(keys.secondaryTecExtraLabelKey),
      axis: 'right',
      format: keys.secondaryFormat,
    };
  }

  return result;
}

export function u340xDetailColumnsForCode(code: U340xKpiLogicalUnitCode): U340xKpiDetailColumnDef[] {
  return DETAIL_COLUMNS[code];
}

/** Normalise les lignes détail API en lignes tableau avec id stable. */
export function u340xNormalizeDetailRows(
  code: U340xKpiLogicalUnitCode,
  rows: Array<Record<string, unknown>>,
): U340xKpiMonthDetailRow[] {
  return rows.map((row, index) => {
    const normalized: U340xKpiMonthDetailRow = { id: `${code}-${index}` };
    for (const col of DETAIL_COLUMNS[code]) {
      const raw = row[col.key];
      if (raw === null || raw === undefined) {
        normalized[col.key] = null;
      } else if (typeof raw === 'boolean' || typeof raw === 'number' || typeof raw === 'string') {
        normalized[col.key] = raw;
      } else {
        normalized[col.key] = String(raw);
      }
    }
    return normalized;
  });
}

/** Statut série après fetch réussi (tableau vide = empty). */
export function u340xSeriesStatusFromLength(length: number): 'empty' | 'ready' {
  return length === 0 ? 'empty' : 'ready';
}
